import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/auth';

// Base URL: usa VITE_API_BASE_URL ou cai pra path /api (dev usa proxy do vite)
const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  (import.meta.env.PROD ? 'https://api.codeshield.dev.br' : '/api');

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // payload de drill pode estourar 30s no tunnel SSH em HWIDs com milhares de eventos
  headers: { 'Content-Type': 'application/json' }
});

// ===== Request: injeta access token =====
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ===== Response: auto-refresh em 401 =====
let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

function processQueue(token: string | null) {
  pendingQueue.forEach(cb => cb(token));
  pendingQueue = [];
}

api.interceptors.response.use(
  r => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (!original || !error.response) return Promise.reject(error);

    // ===== IP mudou no meio da sessao =====
    // Middleware do backend devolve 401 com body { reason: "ip_changed_mid_session" }.
    // NAO eh acesso expirado — eh seguranca: revoga sessao local e redireciona pra
    // tela de IP-change ou login (que ja mostra o aviso).
    const respData = error.response.data as { reason?: string; detail?: string } | undefined;
    if (error.response.status === 401 && respData?.reason === 'ip_changed_mid_session') {
      try { useAuthStore.getState().clearTokens(); } catch { /* ok */ }
      if (typeof window !== 'undefined') {
        // Marca uma flag pra mostrar uma mensagem no login
        sessionStorage.setItem('codeshield_ip_changed', '1');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Nao tenta refresh em rotas de auth
    if (error.response.status === 401 && !original._retry && !original.url?.includes('/auth/')) {
      original._retry = true;
      const store = useAuthStore.getState();
      if (!store.refreshToken) {
        store.clearTokens();
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push((newToken) => {
            if (newToken) {
              original.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(original));
            } else {
              reject(error);
            }
          });
        });
      }
      isRefreshing = true;
      try {
        const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken: store.refreshToken });
        const data = res.data as { accessToken: string; refreshToken: string; accessExpiresAtUtc: string; refreshExpiresAtUtc: string };
        store.setTokens({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          accessExpiresAt: data.accessExpiresAtUtc,
          refreshExpiresAt: data.refreshExpiresAtUtc
        });
        processQueue(data.accessToken);
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch (refreshErr) {
        processQueue(null);
        store.clearTokens();
        // Redireciona pra login
        if (typeof window !== 'undefined') window.location.href = '/login';
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export function unwrapError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { reason?: string; detail?: string } | undefined;
    return data?.reason || data?.detail || err.message;
  }
  return (err as Error)?.message ?? 'unknown_error';
}
